/**
 * The platform's HTTP client.
 *
 * Two things here are load-bearing and worth stating up front:
 *
 * 1. Money and prices are `string`, never `number`. The backend stores money
 *    as int64 micros and prices as int64 at 1e-8; a six-figure crypto price at
 *    that scale needs 16 significant digits and `number` gives 15. Parsing a
 *    balance into a float here would introduce rounding the server went to
 *    considerable trouble to avoid. Arithmetic on money happens on the server.
 *
 * 2. A 401 triggers exactly one refresh attempt, and concurrent callers share
 *    it. Without that, a page that fires six requests on load produces six
 *    refreshes, five of which present an already-rotated token — and the
 *    backend treats refresh-token reuse as theft and revokes the whole family.
 *    Sharing the in-flight refresh is not an optimisation; it is what stops the
 *    client logging itself out.
 */

/** The envelope every service responds with. */
export interface Envelope<T> {
  success: boolean
  data: T
  meta?: PageMeta
  error?: ApiErrorBody
  request?: { id: string }
}

/** Pagination, returned alongside a list rather than inside it. */
export interface PageMeta {
  page: number
  per_page: number
  total: number
  total_pages: number
}

/** A list response with its pagination. */
export interface Page<T> {
  items: T[]
  meta: PageMeta
}

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, string>
}

/** A failed request, carrying the server's own error code. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, string>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details ?? {}
  }

  /** Field-level messages, for rendering next to inputs. */
  fieldError(field: string): string | undefined {
    return this.details[field]
  }

  get isAuth(): boolean {
    return this.status === 401
  }

  /** A refusal the user can act on, as opposed to something being broken. */
  get isBusiness(): boolean {
    return this.status === 422 || this.status === 409
  }
}

/** Tokens as auth-service issues them. */
export interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
}

type TokenListener = (tokens: TokenPair | null) => void

/**
 * Where a session is stored, and why it is not one fixed pair of keys.
 *
 * The trading app and the operations console are separate applications and
 * must never share a session. Writing to the same keys would mean signing into
 * the console silently replaced whatever trader session was already there, and
 * signing out of one signed out of the other.
 *
 * The other direction is worse: an operator who signs into the console and
 * then opens the trading app would be carrying an operator's session into the
 * product they administer, which is the arrangement most likely to produce a
 * trade nobody meant to place.
 *
 * The console calls `api.useNamespace('binaryx.ops')` before it mounts.
 */
let accessKey = 'binaryx.access'
let refreshKey = 'binaryx.refresh'

class ApiClient {
  private access: string | null = null
  private refresh: string | null = null
  private refreshing: Promise<boolean> | null = null
  private sessionExpired: (() => void) | null = null
  private listeners = new Set<TokenListener>()

  // Empty means same origin, which is what the docker stack serves: nginx
  // hands out the SPA and proxies /api on one hostname. When the app is
  // hosted apart from the API - Cloudflare Pages on generworld.com against
  // api.generworld.com - VITE_API_BASE_URL names the API origin at build
  // time and every request below becomes cross-origin, which is why the
  // services carry a CORS allow-list.
  constructor(private readonly baseURL = import.meta.env.VITE_API_BASE_URL ?? '') {
    this.restore()
  }

  // -------------------------------------------------------------------------
  // Tokens
  // -------------------------------------------------------------------------

  /**
   * Moves this client's session into its own storage namespace.
   *
   * Called once, before mounting, by any application that is not the trading
   * app. Storage is re-read afterwards so a session already saved under the
   * new namespace is picked up rather than discarded.
   */
  useNamespace(prefix: string): void {
    accessKey = `${prefix}.access`
    refreshKey = `${prefix}.refresh`
    this.access = null
    this.refresh = null
    this.restore()
  }

  private restore(): void {
    try {
      this.access = localStorage.getItem(accessKey)
      this.refresh = localStorage.getItem(refreshKey)
    } catch {
      // Private browsing, or storage disabled. The session simply does not
      // survive a reload, which is a degradation rather than a failure.
    }
  }

  setTokens(tokens: TokenPair | null): void {
    this.access = tokens?.access_token ?? null
    this.refresh = tokens?.refresh_token ?? null
    try {
      if (tokens) {
        localStorage.setItem(accessKey, tokens.access_token)
        localStorage.setItem(refreshKey, tokens.refresh_token)
      } else {
        localStorage.removeItem(accessKey)
        localStorage.removeItem(refreshKey)
      }
    } catch {
      /* see restore() */
    }
    this.listeners.forEach((fn) => fn(tokens))
  }

  get accessToken(): string | null {
    return this.access
  }

  get isAuthenticated(): boolean {
    return this.access !== null
  }

  /** Notifies on sign-in and sign-out, so the socket can re-authenticate. */
  onTokenChange(fn: TokenListener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  // -------------------------------------------------------------------------
  // Requests
  // -------------------------------------------------------------------------

  get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    return this.request<T>('GET', path + query(params))
  }

  /**
   * GET a list, keeping the pagination that travels beside it.
   *
   * The platform returns `data` as a bare array and pagination in a sibling
   * `meta` object, so a caller that needs the total cannot get it from the
   * payload alone.
   */
  async getPage<T>(path: string, params?: Record<string, unknown>): Promise<Page<T>> {
    const { data, meta } = await this.requestFull<T[]>('GET', path + query(params))
    return {
      items: data ?? [],
      meta: meta ?? {
        page: 1,
        per_page: data?.length ?? 0,
        total: data?.length ?? 0,
        total_pages: 1,
      },
    }
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  /**
   * POST a FormData body, for file uploads.
   *
   * Its own method rather than a branch inside post(), because the header
   * handling is the opposite: a multipart body must NOT have Content-Type set
   * by hand. The browser generates one containing the boundary it chose, and
   * overwriting it with "multipart/form-data" strips that boundary - leaving a
   * request the server cannot parse and an error that points at the file
   * rather than at the header.
   *
   * Everything else - the bearer token, the 401 refresh-and-replay, the
   * envelope unwrapping - is shared, which is why this goes through the same
   * path rather than calling fetch directly.
   */
  postForm<T>(path: string, body: FormData): Promise<T> {
    return this.request<T>('POST', path, body)
  }

  /**
   * GET something that is not JSON.
   *
   * Identity documents, and nothing else so far. Deliberately does not go
   * through requestFull: that method unwraps a JSON envelope and would try to
   * parse a JPEG as one. The token handling is duplicated here rather than
   * abstracted, because the two paths agree on very little beyond it.
   */
  async getBlob(path: string): Promise<Blob> {
    const headers: Record<string, string> = {}
    if (this.access) headers['Authorization'] = `Bearer ${this.access}`

    const response = await fetch(this.baseURL + path, { headers })
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as
        | { error?: { code: string; message: string } }
        | null
      throw new ApiError(
        response.status,
        envelope?.error ?? { code: 'unexpected_error', message: 'Could not open the document.' },
      )
    }
    return response.blob()
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body)
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('DELETE', path, body)
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    retried = false,
  ): Promise<T> {
    const { data } = await this.requestFull<T>(method, path, body, retried)
    return data
  }

  private async requestFull<T>(
    method: string,
    path: string,
    body?: unknown,
    retried = false,
  ): Promise<{ data: T; meta?: PageMeta }> {
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData

    const headers: Record<string, string> = { Accept: 'application/json' }
    // Deliberately not set for FormData: see postForm above.
    if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
    if (this.access) headers['Authorization'] = `Bearer ${this.access}`

    let response: Response
    try {
      response = await fetch(this.baseURL + path, {
        method,
        headers,
        body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      })
    } catch (cause) {
      // A network failure is not an API error: there is no code and no status
      // to act on, so it is reported as its own thing.
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Could not reach the server. Check your connection.',
      })
    }

    // 401 on anything except the refresh call itself: try once to renew, then
    // replay the original request.
    if (response.status === 401 && !retried && !path.includes('/auth/refresh')) {
      if (await this.renew()) {
        return this.requestFull<T>(method, path, body, true)
      }
    }

    const envelope = (await response.json().catch(() => null)) as Envelope<T> | null

    if (!response.ok || !envelope?.success) {
      throw new ApiError(
        response.status,
        envelope?.error ?? {
          code: 'unexpected_error',
          message: 'Something went wrong. Please try again.',
        },
      )
    }
    return { data: envelope.data, meta: envelope.meta }
  }

  /**
   * Renews the access token, sharing one attempt across concurrent callers.
   *
   * The backend rotates refresh tokens and treats reuse as theft — it revokes
   * the entire token family. Six parallel 401s each firing their own refresh
   * would present the same token six times and sign the user out completely,
   * so the in-flight promise is shared rather than duplicated.
   */
  /**
   * Registers what to do when the session is over for good.
   *
   * Fired only when the server rejects the refresh token, never on a network
   * error - a blip must not sign anybody out. Without this the end of a
   * session is invisible: the tokens are cleared, every later call 401s, and
   * the trader is left on a terminal whose buttons do nothing and whose
   * balance never updates, with no way to tell that signing in again is the
   * answer. That is exactly what happens when the signing key is rotated
   * underneath a live browser tab.
   */
  onSessionExpired(handler: () => void): void {
    this.sessionExpired = handler
  }

  private renew(): Promise<boolean> {
    if (this.refreshing) return this.refreshing

    this.refreshing = (async () => {
      if (!this.refresh) return false
      try {
        const response = await fetch(this.baseURL + '/api/v1/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: this.refresh }),
        })
        const envelope = (await response.json().catch(() => null)) as Envelope<TokenPair> | null
        if (!response.ok || !envelope?.success) {
          // The server refused the refresh token: this session cannot be
          // recovered, as opposed to the network failure handled below.
          this.setTokens(null)
          this.sessionExpired?.()
          return false
        }
        this.setTokens(envelope.data)
        return true
      } catch {
        // A network blip must not sign the user out: their refresh token is
        // probably still perfectly valid.
        return false
      } finally {
        this.refreshing = null
      }
    })()

    return this.refreshing
  }
}

function query(params?: Record<string, unknown>): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

export const api = new ApiClient()

// ---------------------------------------------------------------------------
// Formatting
//
// Display only. None of these feed back into a request — the string the server
// sent is what gets sent back.
// ---------------------------------------------------------------------------

/** Formats a money string for display, keeping its exact value. */
export function formatMoney(amount: string | undefined, currency = 'USD'): string {
  if (!amount) return '—'
  const negative = amount.startsWith('-')
  const [whole = '0', fraction = '00'] = amount.replace('-', '').split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const symbol = currency === 'USD' ? '$' : `${currency} `
  return `${negative ? '−' : ''}${symbol}${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`
}

/** Formats a signed profit or loss, with an explicit sign. */
export function formatPnL(amount: string | undefined): string {
  if (!amount) return '—'
  const formatted = formatMoney(amount)
  return amount.startsWith('-') ? formatted : `+${formatted}`
}

/** Formats a countdown as m:ss. */
export function formatCountdown(seconds: number): string {
  if (seconds <= 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Renders a payout rate in basis points as a percentage. */
export function formatPayout(bps: number): string {
  return `${(bps / 100).toFixed(0)}%`
}
